import { useRef, useState, useEffect, useCallback } from 'react';

import {
  growthMotionFilterOptions,
  softwareSectorFilterOptions,
  primaryCustomerFilterOptions
} from 'utils/constants';
import {
  FilterType,
  SwitchType,
  FilterOption
} from 'interfaces/components/filter';
import Button from 'components/Button';
import Switch from 'components/Switch';
import { Filter } from 'components/Filter';
import DownIcon from 'assets/icons/down.svg';
import { useAuth } from 'context/AuthContext';
import {
  applyFilters,
  resetFilters
} from 'store/chartFilters/chartFilters.slice';
import {
  resetSwitches,
  handleXAxisSwitch,
  handleTimeDurationSwitch,
  handleQuartileMedianSwitch
} from 'store/chartSwitches/chartSwitches.slice';
import FilterIcon from 'assets/icons/filter.svg';
import { useClickAway } from '@uidotdev/usehooks';
import ResetIcon from 'assets/icons/reset-gray.svg';
import DarkDownIcon from 'assets/icons/down-white.svg';
import ButtonWithIcon from 'components/ButtonWithIcon';
import { RootState, useAppDispatch, useAppSelector } from 'store';
import useClickOutside from 'components/ModalTextField/ColorPicker/useClickOutside';

interface ChartFiltersProps {
  chartTitle: string;
  isAvgDealSizeSwitchDisabled: boolean;
  isQuartileMedianSwitchDisabled: boolean;
}

const ChartFilters = ({
  chartTitle,
  isAvgDealSizeSwitchDisabled,
  isQuartileMedianSwitchDisabled
}: ChartFiltersProps) => {
  const popover = useRef(null);
  const { isPortCo } = useAuth();
  const dispatch = useAppDispatch();
  const [count, setCount] = useState(0);
  const [isOpen, toggle] = useState(false);
  const isNewLogoVelocityChart = chartTitle === 'New Logo Velocity';
  const isTimeDurationSwitchDisabled = isNewLogoVelocityChart;
  const [growthMotion, setGrowthMotion] = useState<FilterOption[]>([]);
  const [softwareSector, setSoftwareSector] = useState<FilterOption[]>([]);
  const [primaryCustomer, setPrimaryCustomer] = useState<FilterOption[]>([]);
  const chartFilters = useAppSelector((state: RootState) => state.chartFilters);
  const chartSwitch = useAppSelector((state: RootState) => state.chartSwitches);
  const [xAxisSwitch, setXAxisSwitch] = useState(() => chartSwitch.xAxisSwitch);
  const [timeDurationSwitch, setTimeDurationSwitch] = useState(
    () => chartSwitch.timeDurationSwitch
  );
  const [quartileMedianSwitch, setQuartileMedianSwitch] = useState(
    () => chartSwitch.quartileMedianSwitch
  );

  const close = useCallback(() => toggle(false), []);

  useClickOutside(popover, close);

  const disabledFilters = useAppSelector(
    (state: RootState) => state.disabledFilters.disabledFilters
  );

  const isSoftwareSectorEnabled = isNewLogoVelocityChart
    ? false
    : !disabledFilters[chartTitle]?.softwareSector;

  const isGrowthMotionEnabled = isNewLogoVelocityChart
    ? false
    : !disabledFilters[chartTitle]?.growthMotion;

  const isPrimaryCustomerEnabled =
    !disabledFilters[chartTitle]?.primaryCustomer;

  const createClickAwayHandler = useCallback((index: number) => {
    return () => {
      const parent =
        document.getElementsByClassName('dropdown-container')[index];
      const elem = document.getElementsByClassName('dropdown-heading')[index];
      if (elem && parent && parent.ariaExpanded === 'true') {
        (elem as HTMLElement).click();
      }
    };
  }, []);

  const softwareSectorRef = useClickAway<HTMLInputElement>(
    createClickAwayHandler(0)
  );

  const growthMotionRef = useClickAway<HTMLInputElement>(
    createClickAwayHandler(1)
  );

  const primaryCustomerRef = useClickAway<HTMLInputElement>(
    createClickAwayHandler(2)
  );

  const getStoredFilter = useCallback((key: FilterType): FilterOption[] => {
    try {
      const storedValue = window.sessionStorage.getItem(key);

      return storedValue ? JSON.parse(storedValue) : [];
    } catch (error) {
      console.error(`Error parsing stored filter ${key}:`, error);

      return [];
    }
  }, []);

  const getStoredSwitch = useCallback((key: SwitchType): boolean => {
    try {
      const storedValue = window.sessionStorage.getItem(key);

      return storedValue
        ? JSON.parse(storedValue)
        : key === 'quartile_median'
        ? false
        : true;
    } catch (error) {
      console.error(`Error parsing stored switch ${key}:`, error);

      return key === 'quartile_median' ? false : true;
    }
  }, []);

  const setStoredFilter = useCallback(
    (key: FilterType, value: FilterOption[]) => {
      try {
        window.sessionStorage.setItem(key, JSON.stringify(value));
      } catch (error) {
        console.error(`Error storing filter ${key}:`, error);
      }
    },
    []
  );

  const setStoredSwitch = useCallback((key: SwitchType, value: boolean) => {
    try {
      window.sessionStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error(`Error storing switch ${key}:`, error);
    }
  }, []);

  const onSoftwareSectorChange = useCallback((value: FilterOption[]) => {
    setSoftwareSector(value);
  }, []);

  const onGrowthMotionChange = useCallback((value: FilterOption[]) => {
    setGrowthMotion(value);
  }, []);

  const onPrimaryCustomerChange = useCallback((value: FilterOption[]) => {
    setPrimaryCustomer(value);
  }, []);

  const onXAxisSwitchChange = useCallback(
    (value: boolean) => {
      if (!isAvgDealSizeSwitchDisabled) {
        setXAxisSwitch(value);
      }
    },
    [isAvgDealSizeSwitchDisabled]
  );

  const onTimeDurationSwitchChange = useCallback(
    (value: boolean) => {
      if (!isTimeDurationSwitchDisabled) {
        setTimeDurationSwitch(value);
      }
    },
    [isTimeDurationSwitchDisabled]
  );

  const onQuartileMedianSwitchChange = useCallback(
    (value: boolean) => {
      if (!isQuartileMedianSwitchDisabled) {
        setQuartileMedianSwitch(value);
      }
    },
    [isQuartileMedianSwitchDisabled]
  );

  const onResetFilters = () => {
    const resetSoftwareSector: FilterOption[] = isNewLogoVelocityChart
      ? softwareSectorFilterOptions
      : [];

    const resetGrowthMotion: FilterOption[] = isNewLogoVelocityChart
      ? growthMotionFilterOptions
      : [];

    const resetPrimaryCustomer: FilterOption[] = [] as FilterOption[];

    setXAxisSwitch(true);
    setTimeDurationSwitch(true);
    setQuartileMedianSwitch(false);
    setGrowthMotion(resetGrowthMotion);
    setSoftwareSector(resetSoftwareSector);
    setPrimaryCustomer(resetPrimaryCustomer);

    dispatch(resetFilters());
    dispatch(resetSwitches());

    setStoredSwitch('x_axis', true);
    setStoredSwitch('time_duration', true);
    setStoredSwitch('quartile_median', false);

    setStoredFilter('growth_motion', resetGrowthMotion);
    setStoredFilter('software_sector', resetSoftwareSector);
    setStoredFilter('primary_customer', resetPrimaryCustomer);

    window.sessionStorage.setItem('is_filters_applied', JSON.stringify(false));

    toggle(false);
  };

  const onhandleApply = () => {
    window.sessionStorage.setItem('is_filters_applied', JSON.stringify(true));

    const softwareSectorToApply = isNewLogoVelocityChart
      ? softwareSectorFilterOptions
      : softwareSector;

    const growthMotionToApply = isNewLogoVelocityChart
      ? growthMotionFilterOptions
      : growthMotion;

    const timeDurationToApply = isTimeDurationSwitchDisabled
      ? true
      : timeDurationSwitch;

    if (isNewLogoVelocityChart) {
      setStoredSwitch('time_duration', timeDurationToApply);
      setStoredFilter('growth_motion', growthMotionToApply);
      setStoredFilter('software_sector', softwareSectorToApply);
    } else {
      if (isSoftwareSectorEnabled) {
        setStoredFilter('software_sector', softwareSectorToApply);
      }

      if (isGrowthMotionEnabled) {
        setStoredFilter('growth_motion', growthMotionToApply);
      }

      if (!isTimeDurationSwitchDisabled) {
        setStoredSwitch('time_duration', timeDurationToApply);
      }
    }

    if (isPrimaryCustomerEnabled) {
      setStoredFilter('primary_customer', primaryCustomer);
    }

    if (!isAvgDealSizeSwitchDisabled) {
      setStoredSwitch('x_axis', xAxisSwitch);
    }

    if (!isQuartileMedianSwitchDisabled) {
      setStoredSwitch('quartile_median', quartileMedianSwitch);
    }

    dispatch(
      applyFilters({
        primaryCustomer,
        growthMotion: growthMotionToApply,
        softwareSector: softwareSectorToApply
      })
    );

    dispatch(handleXAxisSwitch(xAxisSwitch));
    dispatch(handleTimeDurationSwitch(timeDurationSwitch));
    dispatch(handleQuartileMedianSwitch(quartileMedianSwitch));

    toggle(false);
  };

  useEffect(() => {
    const storedGrowthMotion = getStoredFilter('growth_motion');
    const storedSoftwareSector = getStoredFilter('software_sector');
    const storedPrimaryCustomer = getStoredFilter('primary_customer');

    const storedXAxisSwitch = getStoredSwitch('x_axis');
    const storedTimeDurationSwitch = getStoredSwitch('time_duration');
    const storedQuartileMedianSwitch = getStoredSwitch('quartile_median');

    const isFiltersApplied =
      window.sessionStorage.getItem('is_filters_applied') === 'true';

    if (isNewLogoVelocityChart) {
      setGrowthMotion(growthMotionFilterOptions);
      setSoftwareSector(softwareSectorFilterOptions);
    } else {
      if (isSoftwareSectorEnabled) {
        setSoftwareSector(storedSoftwareSector);
      }

      if (isGrowthMotionEnabled) {
        setGrowthMotion(storedGrowthMotion);
      }
    }

    if (isPrimaryCustomerEnabled) {
      setPrimaryCustomer(storedPrimaryCustomer);
    }

    if (!isTimeDurationSwitchDisabled) {
      setTimeDurationSwitch(storedTimeDurationSwitch);
    }

    if (!isQuartileMedianSwitchDisabled) {
      setQuartileMedianSwitch(storedQuartileMedianSwitch);
    }

    if (!isAvgDealSizeSwitchDisabled) {
      setXAxisSwitch(storedXAxisSwitch);
    }

    if (isFiltersApplied) {
      dispatch(
        applyFilters({
          growthMotion: isGrowthMotionEnabled ? storedGrowthMotion : [],
          softwareSector: isSoftwareSectorEnabled ? storedSoftwareSector : [],
          primaryCustomer: isPrimaryCustomerEnabled ? storedPrimaryCustomer : []
        })
      );

      dispatch(handleXAxisSwitch(storedXAxisSwitch));
      dispatch(handleTimeDurationSwitch(storedTimeDurationSwitch));
      dispatch(handleQuartileMedianSwitch(storedQuartileMedianSwitch));
    }
  }, [
    dispatch,
    getStoredFilter,
    getStoredSwitch,
    isGrowthMotionEnabled,
    isNewLogoVelocityChart,
    isSoftwareSectorEnabled,
    isPrimaryCustomerEnabled,
    isAvgDealSizeSwitchDisabled,
    isTimeDurationSwitchDisabled,
    isQuartileMedianSwitchDisabled
  ]);

  useEffect(() => {
    let filtersCount = 0;

    if (
      isGrowthMotionEnabled &&
      chartFilters.growthMotion.length > 0 &&
      chartFilters.growthMotion.length !== growthMotionFilterOptions.length
    ) {
      filtersCount++;
    }

    if (
      isSoftwareSectorEnabled &&
      chartFilters.softwareSector.length > 0 &&
      chartFilters.softwareSector.length !== softwareSectorFilterOptions.length
    ) {
      filtersCount++;
    }

    if (
      isPrimaryCustomerEnabled &&
      chartFilters.primaryCustomer.length > 0 &&
      chartFilters.primaryCustomer.length !==
        primaryCustomerFilterOptions.length
    ) {
      filtersCount++;
    }

    if (!isAvgDealSizeSwitchDisabled && !chartSwitch.xAxisSwitch) {
      filtersCount++;
    }

    if (!isTimeDurationSwitchDisabled && !chartSwitch.timeDurationSwitch) {
      filtersCount++;
    }

    if (!isQuartileMedianSwitchDisabled && chartSwitch.quartileMedianSwitch) {
      filtersCount++;
    }

    setCount(filtersCount);
  }, [
    chartSwitch,
    chartFilters,
    isGrowthMotionEnabled,
    isSoftwareSectorEnabled,
    isPrimaryCustomerEnabled,
    isAvgDealSizeSwitchDisabled,
    isTimeDurationSwitchDisabled,
    isQuartileMedianSwitchDisabled
  ]);

  return (
    <>
      <div
        onClick={() => toggle(true)}
        className={`flex items-center justify-between gap-1 w-[140px] h-10 px-3 border border-gray-300 rounded-full cursor-pointer select-none${
          isOpen ? ' border-black' : ''
        }`}
      >
        <div className='flex items-center gap-1'>
          <img src={FilterIcon} alt='down' />
          Filters
          {!!count && (
            <div className='flex items-center justify-center w-5 h-5 rounded-full bg-green-700 text-white'>
              {count}
            </div>
          )}
        </div>

        <picture className={isOpen ? 'rotate-180' : ''}>
          <source srcSet={DarkDownIcon} media='(prefers-color-scheme: dark)' />

          <img src={DownIcon} alt='down' />
        </picture>
      </div>

      {isOpen && (
        <div
          ref={popover}
          className={`absolute top-11 z-[200] flex flex-col w-[675px] h-[420px] bg-white dark:bg-black rounded-lg border border-gray-300 shadow-popover${
            isPortCo ? ' left-[216px]' : ' left-0'
          }`}
        >
          <div className='flex items-stretch flex-1'>
            <div className='flex flex-col gap-4 flex-1 p-6 border-r border-gray-300'>
              <p className='font-semibold'>Benchmark Filters</p>

              <Switch
                id='quartile-median-switch'
                options={['Top Quartile', 'Median']}
                disabled={isQuartileMedianSwitchDisabled}
                handleChange={onQuartileMedianSwitchChange}
                bgColors={['bg-[#4DB96B]', 'bg-[#70A7B2]']}
                value={
                  isQuartileMedianSwitchDisabled ? false : quartileMedianSwitch
                }
              />

              <Switch
                id='time-duration-switch'
                options={['Last 4 Years', 'All Time']}
                disabled={isTimeDurationSwitchDisabled}
                handleChange={onTimeDurationSwitchChange}
                value={isTimeDurationSwitchDisabled ? true : timeDurationSwitch}
              />

              <Filter
                value={softwareSector}
                label='Software Sector'
                ref={softwareSectorRef}
                id='software_sector_filter'
                onChange={onSoftwareSectorChange}
                options={softwareSectorFilterOptions}
                isDisabled={!isSoftwareSectorEnabled}
              />

              <Filter
                value={growthMotion}
                label='Growth Motion'
                ref={growthMotionRef}
                id='growth_motion_filter'
                onChange={onGrowthMotionChange}
                options={growthMotionFilterOptions}
                isDisabled={!isGrowthMotionEnabled}
              />

              <Filter
                id='customer_filter'
                value={primaryCustomer}
                label='Customer Segment'
                ref={primaryCustomerRef}
                onChange={onPrimaryCustomerChange}
                options={primaryCustomerFilterOptions}
                isDisabled={!isPrimaryCustomerEnabled}
              />
            </div>

            <div className='flex flex-col gap-4 flex-1 p-6'>
              <p className='font-semibold'>Chart Filters</p>

              <Switch
                id='x-axis-switch'
                handleChange={onXAxisSwitchChange}
                disabled={isAvgDealSizeSwitchDisabled}
                options={['Avg. Deal Size', 'ARR Range']}
                value={isAvgDealSizeSwitchDisabled ? true : xAxisSwitch}
              />
            </div>
          </div>

          <div className='flex items-center justify-between gap-4 h-[70px] px-6 py-4 border-t border-gray-300'>
            <ButtonWithIcon
              id='resetFilters'
              label='Reset Filters'
              prefixIcon={ResetIcon}
              onClick={onResetFilters}
              prefixIconAlt='Reset Icon'
              className='flex items-center px-4 py-2 whitespace-nowrap cursor-pointer hover:bg-gray-200 rounded-full duration-300'
            />

            <Button
              id='applyFilters'
              label='Apply Filters'
              onClick={onhandleApply}
              className='px-4 py-2 rounded-full bg-green-500 hover:bg-green-300 text-black duration-300'
            />
          </div>
        </div>
      )}
    </>
  );
};

export default ChartFilters;
